import { Button, Card, Spinner, useToast } from "@closer/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";

/**
 * Davet ekranı (CLOS-74 `[035]`).
 *
 * ### Neden var
 *
 * Davet e-postası `${WEB_ORIGIN}/davet?token=...` adresine yönlendiriyordu ama bu rota
 * **hiç yoktu**: catch-all `/app`'e atıyor, token sessizce düşüyordu. Kartın üç ucu da
 * çalışıyor ve testleri yeşildi; kopuk olan tek halka buydu ve davet edilen hiç kimse
 * organizasyona katılamıyordu. Uçların yeşil olması akışın çalıştığı anlamına gelmiyor.
 *
 * ### Oturum GEREKTİRMİYOR
 *
 * Davetin kime ve hangi organizasyona ait olduğu kaydolmadan önce görülebilmeli —
 * sunucudaki `GET /api/invitations` de bu yüzden oturumsuz. Rota bu yüzden `RequireAuth`
 * ağacının DIŞINDA duruyor: içeride olsaydı davet edilen kişi `/login`'e atılır ve
 * token yolda kaybolurdu.
 *
 * ### Kabul/ret kararını SUNUCU verir
 *
 * Bu ekran e-posta uyuşmazlığını yalnız UYARI olarak gösteriyor, butonu kapatmıyor.
 * Sunucu karşılaştırmayı `toLocaleLowerCase("tr")` ile yapıyor; `I` harfi tarayıcının
 * `toLowerCase()`'inde `i`, sunucuda `ı` oluyor. İstemci kapı olsaydı geçerli bir daveti
 * kabul etmeye çalışan kullanıcının butonu sebepsiz kapalı kalırdı. Guard'lardaki ayrımın
 * aynısı: istemci kolaylık sağlar, kapı sunucudadır.
 */

interface Davet {
  id: string;
  organizationName: string;
  email: string;
  roleName: string;
  expiresAt: string;
  status: string;
}

type Durum = "yukleniyor" | "hazir" | "gecersiz" | "islemde" | "reddedildi";

export function InvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [davet, setDavet] = useState<Davet | null>(null);
  const [durum, setDurum] = useState<Durum>(token ? "yukleniyor" : "gecersiz");
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const { data } = await api<Davet>(`/api/invitations?token=${encodeURIComponent(token)}`);
        setDavet(data);
        setDurum("hazir");
      } catch {
        /**
         * Var olmayan davet ile süresi dolmuş davet sunucuda AYNI cevaba düşüyor
         * (token deneyen biri hangisinin gerçek olduğunu öğrenmesin). Arayüz de ayırt
         * ediyormuş gibi davranmamalı: kullanıcının atacağı adım ikisinde de aynı —
         * yeni davet istemek.
         */
        setDurum("gecersiz");
      }
    })();
  }, [token]);

  async function yanitla(karar: "accept" | "reject") {
    if (!token) return;
    setDurum("islemde");
    setHata(null);
    try {
      await api(`/api/invitations/${karar}`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });

      if (karar === "reject") {
        setDurum("reddedildi");
        return;
      }

      /**
       * Üyelik listesi yeniden okunuyor: önbellek eski listeyi tutuyor ve kullanıcı
       * yeni organizasyonuna sayfayı elle yenileyene kadar ulaşamazdı.
       */
      await queryClient.invalidateQueries({ queryKey: ["memberships"] });
      toast.success("Davet kabul edildi");
      navigate("/tenants");
    } catch (error) {
      /**
       * Metin SUNUCUDAN geliyor (kriter 4). Buradaki redlerin gerçek ve farklı sebepleri
       * var — e-posta doğrulanmamış, davet başkasına ait, kapasite dolmuş — ve
       * kullanıcının atacağı adım her birinde farklı. Hepsini tek bir genel cümleye
       * çevirmek kullanıcıyı çıkmaza sokardı.
       */
      setHata(
        error instanceof ApiRequestError
          ? error.message
          : "Davet yanıtlanamadı. Lütfen tekrar deneyin.",
      );
      setDurum("hazir");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <Card title="Organizasyon Daveti" className="w-full max-w-sm">
        {durum === "yukleniyor" && (
          <div role="status" className="flex flex-col items-center gap-3 py-4">
            <Spinner size="lg" />
            <p className="text-sm text-fg-muted">Davet kontrol ediliyor…</p>
          </div>
        )}

        {durum === "gecersiz" && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Bu davet geçerli değil. Bağlantının süresi dolmuş, davet iptal edilmiş ya da daha önce
              yanıtlanmış olabilir. Sizi davet eden kişiden yeni bir davet isteyebilirsiniz.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {durum === "reddedildi" && davet && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Daveti reddettiniz. <strong>{davet.organizationName}</strong> organizasyonuna
              katılmadınız.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {(durum === "hazir" || durum === "islemde") && davet && (
          <DavetKarti
            davet={davet}
            token={token ?? ""}
            kullaniciEposta={user?.email ?? null}
            epostaDogrulandi={user?.emailVerified ?? false}
            islemde={durum === "islemde"}
            hata={hata}
            onYanit={yanitla}
          />
        )}
      </Card>
    </div>
  );
}

function DavetKarti({
  davet,
  token,
  kullaniciEposta,
  epostaDogrulandi,
  islemde,
  hata,
  onYanit,
}: {
  davet: Davet;
  token: string;
  kullaniciEposta: string | null;
  epostaDogrulandi: boolean;
  islemde: boolean;
  hata: string | null;
  onYanit: (karar: "accept" | "reject") => void;
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  /**
   * Yanlış hesapla girişli kullanıcının kaçış yolu (CLOS-496 sınıfı, bug avı).
   *
   * Davet başka bir e-postaya aitse sunucu kabulü reddediyor; ekranda çıkış yolu yoksa
   * kullanıcı accept'e basıp hata görüp aynı ekranda kilitleniyordu. Çıkıp DOĞRU hesapla
   * girebilmeli; davet token'ı /login'e taşınıyor ki giriş sonrası davete geri dönsün.
   */
  const hesapDegistir = async (): Promise<void> => {
    try {
      await logout();
      navigate(`/login?davet=${encodeURIComponent(token)}`);
    } catch {
      toast.error("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  const baslik = (
    <div className="flex flex-col gap-1">
      <p className="text-sm text-fg-muted">
        <strong className="text-fg">{davet.organizationName}</strong> organizasyonuna davet
        edildiniz.
      </p>
      <p className="text-sm text-fg-soft">Rol: {davet.roleName}</p>
    </div>
  );

  /**
   * Oturum yoksa hesap adımına yönlendiriliyor — kartın "mevcut veya yeni hesapla
   * eşleştirme" kapsamı.
   *
   * Hangi e-postayla giriş yapılması gerektiği AÇIKÇA yazılıyor: sunucu, oturumdaki
   * e-posta davetinkiyle uyuşmazsa reddediyor ve bu bilgi verilmezse kullanıcı yanlış
   * hesapla girip sebebini anlamadan reddedilirdi. Adres zaten davet bağlantısını elinde
   * tutan herkese `GET /api/invitations` ile açık; ekran yeni bir şey ifşa etmiyor.
   */
  if (!kullaniciEposta) {
    const donus = `davet=${encodeURIComponent(token)}`;
    return (
      <div className="flex flex-col gap-4">
        {baslik}
        <p className="text-sm text-fg-muted">
          Daveti yanıtlamak için <strong>{davet.email}</strong> adresiyle giriş yapın. Bu adresle
          hesabınız yoksa önce kayıt olun; işlem bitince bu sayfaya geri dönersiniz.
        </p>
        <Link
          to={`/login?${donus}`}
          className="text-center font-medium text-primary hover:underline"
        >
          Giriş yap
        </Link>
        <Link
          to={`/register?${donus}`}
          className="text-center font-medium text-primary hover:underline"
        >
          Kayıt ol
        </Link>
      </div>
    );
  }

  /**
   * Artık istemci tarafında "başka hesap" KARŞILAŞTIRMASI yapılmıyor.
   *
   * CLOS-74 kararı 2 ile sunucu davet e-postasını MASKELİ dönüyor (`d***@firma.com`);
   * elimizde tam adres olmadığı için oturumdaki e-postayla eşleşip eşleşmediğini
   * güvenilir biçimde söyleyemeyiz — denesek maskeyle karşılaştırma hep "uyuşmuyor"
   * derdi ve doğru hesaptaki kullanıcıya yanlış uyarı verirdi. Bunun yerine hangi
   * adresin beklendiğini HATIRLATIYORUZ; kabul/ret kararını sunucu veriyor ve uyuşmazsa
   * dönen hata mesajı gösteriliyor (zaten öyleydi — kapı hep sunucudaydı).
   */
  return (
    <div className="flex flex-col gap-4">
      {baslik}

      <p role="status" className="text-sm text-fg-muted">
        Bu davet <strong>{davet.email}</strong> adresine gönderildi; yanıtlamak için o hesapla giriş
        yapmış olmalısınız. Şu an <strong>{kullaniciEposta}</strong> ile giriştesiniz.
      </p>

      {/**
       * Doğrulanmamış hesabın kabulünü SUNUCU reddediyor (kartın CLOS-62 önkoşulu:
       * "davet işlemi yalnız doğrulanmış ... e-posta kimliği üzerinden tamamlanır").
       * Yalnız hata metnini göstermek kullanıcıyı çıkmazda bırakırdı — yapabileceği tek
       * şeye bir yol veriliyor. `RequireVerifiedEmail`'deki ayrımın aynısı: bu bir
       * kolaylık, kapı sunucuda.
       */}
      {!epostaDogrulandi && (
        <p className="text-sm text-fg-muted">
          Daveti kabul etmek için önce e-posta adresinizi doğrulamalısınız.{" "}
          <Link to="/verify-email" className="font-medium text-primary hover:underline">
            E-postamı doğrula
          </Link>
        </p>
      )}

      {hata && (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      )}

      <Button onClick={() => onYanit("accept")} loading={islemde}>
        Daveti kabul et
      </Button>
      <Button variant="secondary" onClick={() => onYanit("reject")} disabled={islemde}>
        Reddet
      </Button>

      {/* Kaçış yolu: davet başka hesaba aitse kullanıcı buradan çıkıp doğru hesapla
          girer; token korunduğu için giriş sonrası davete geri döner. */}
      <p className="text-center text-sm text-fg-soft">
        Bu hesap değil mi?{" "}
        <Button variant="ghost" onClick={() => void hesapDegistir()} disabled={islemde}>
          Farklı hesapla giriş yap
        </Button>
      </p>
    </div>
  );
}
